import { shallowEqual } from 'react-redux';

import { Spacer } from '@zillow/constellation';
import { Anchor, Label } from '@zillow/constellation-10';

import { useSelector } from '../../../../selectors/useSelector';

import accessUtils from '../../../../utils/accessUtils';

import SingleFamilyCSEvents from '../../../../model/ClickstreamEvent/TotalMonthlyPriceWithFee/SingleFamilyCSEvents';

import AcceptZillowApplicationsFieldSet from '../../../../components/WizardModularListing/AcceptZillowApplications/components/AcceptZillowApplicationsFieldSet';
import PetPolicyFormField from '../../../../components/WizardModularListing/PetPolicy/components/PetPolicyFormField';
import ListingTourFormField from '../../../../components/WizardModularListing/ListingTour/components/ListingTourFormField';
import HideAddressFieldSet from '../../../../components/WizardModularListing/HidePropertyAddress/components/HideAddressFieldSet';
import AmenitiesWithAdditionalAmenitiesFields from '../../../../components/WizardModularListing/AmenitiesProperty/components/AmenitiesWithAdditionalAmenitiesFields';
import SquareFootageFormField from '../../../../components/WizardModularListing/PropertyDescription/components/SquareFootageFormField';
import PhotoUploadFormField from '../../../../components/WizardModularListing/AddPhotos/components/PhotoUploadFormField';
import AllowContactByPhoneFieldSet from '../../../../components/WizardModularListing/AllowContactByPhone/components/AllowContactByPhoneFieldSet';
import CostsAndFeesFormField from '../../CostsAndFees/components/CostsAndFeesFormField';
import SFCostAndFeesTotalMonthlyPriceWithFeeAlert from '../../../Alerts/SFCostAndFeesTotalMonthlyPriceWithFeeAlert';
import SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert from '../../../Alerts/SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert';

import { WizardBody, WizardFormSectionContainer, WizardHeading } from '../../../../styles/wizard';

const FormSectionContainer = () => {
  const {
    appConfigCanAccessTotalMonthlyPriceWithFee,
    cookieEnabledServices,
    currentProperty,
    isOptInTotalMonthlyPriceWithFeeEligible,
    isTotalMonthlyPriceWithFeeEligible,
    isUpdateRestricted: isApplicationsUpdateRestricted,
    relist: {
      fieldsToRender: {
        renderSquareFootageField,
        renderAcceptApplicationsField,
        renderPetsField,
        renderPhotosField,
        renderToursField,
        renderAmenitiesField,
        renderHidePropertyAddressField,
        renderAllowContactByPhoneField,
        renderCostsAndFeesField,
      },
    },
    user,
  } = useSelector((state) => {
    return {
      appConfigCanAccessTotalMonthlyPriceWithFee: state.app.appConfig.config.features.canAccessTotalMonthlyPriceWithFee,
      cookieEnabledServices: state?.app?.cookieEnabledServices,
      currentProperty: state?.properties?.currentProperty,
      isOptInTotalMonthlyPriceWithFeeEligible:
        state.properties.currentProperty?.totalMonthlyPriceWithFee.isOptInEligible,
      isTotalMonthlyPriceWithFeeEligible: state.properties.currentProperty?.totalMonthlyPriceWithFeeNotice?.isEligible,
      isUpdateRestricted: state.properties.currentProperty.restrictions.applications.isUpdateRestricted,
      relist: state.relist,
      user: state?.user,
    };
  }, shallowEqual);

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    cookieEnabledServices,
    canAccessTotalMonthlyPriceWithFee: appConfigCanAccessTotalMonthlyPriceWithFee,
    isEligible: isTotalMonthlyPriceWithFeeEligible,
  });

  return (
    <WizardFormSectionContainer data-testid="FormSectionContainer">
      <WizardHeading level={1} textStyle="heading-sm">
        Consider adding additional details
      </WizardHeading>
      <WizardBody>Review and update any information that has changed since you last listed this property.</WizardBody>
      {renderSquareFootageField && (
        <Spacer marginTop={{ default: 'md', lg: 'lg' }} marginBottom="xxs">
          <SquareFootageFormField isRelistingFieldset={true} />
        </Spacer>
      )}
      {renderPetsField && (
        <Spacer marginTop={{ default: 'md', lg: 'lg' }} marginBottom="xxs">
          <PetPolicyFormField isRelistingFieldset={true} />
        </Spacer>
      )}
      {renderPhotosField && (
        <Spacer marginTop={{ default: 'md', lg: 'lg' }} marginBottom="xxs">
          <Spacer paddingBottom="xxs">
            <Label size="lg">Photos</Label>
          </Spacer>
          <PhotoUploadFormField />
        </Spacer>
      )}
      {renderToursField && (
        <Spacer marginTop={{ default: 'md', lg: 'lg' }} marginBottom="xxs">
          <Spacer paddingBottom="xxs">
            <Label size="lg">3D tour</Label>
          </Spacer>
          <ListingTourFormField isRelistingFieldset={true} />
        </Spacer>
      )}
      {renderAmenitiesField && (
        <Spacer marginTop={{ default: 'md', lg: 'lg' }} marginBottom="xxs">
          <Spacer paddingBottom="xxs">
            <Label size="lg">Amenities</Label>
            <Spacer marginTop="xxs" />
            <WizardBody>Sharing more will help renters see themselves in your home.</WizardBody>
          </Spacer>
          <AmenitiesWithAdditionalAmenitiesFields isRelistingFieldset={true} />
        </Spacer>
      )}
      {renderHidePropertyAddressField && (
        <Spacer marginTop={{ default: 'md', lg: 'lg' }} marginBottom="xxs">
          <HideAddressFieldSet isRelistingFieldset={true} />
        </Spacer>
      )}
      {renderAllowContactByPhoneField && (
        <Spacer marginTop={{ default: 'md', lg: 'lg' }} marginBottom="xxs">
          <AllowContactByPhoneFieldSet isRelistingFieldset={true} />
        </Spacer>
      )}
      {Boolean(isApplicationsUpdateRestricted === false && renderAcceptApplicationsField) && (
        <Spacer marginTop={{ default: 'md', lg: 'lg' }} marginBottom="xxs">
          <AcceptZillowApplicationsFieldSet isRelistingFieldset={true} />
        </Spacer>
      )}
      {renderCostsAndFeesField && (
        <Spacer marginTop={{ default: 'md', lg: 'lg' }}>
          <Label size="lg">What additional costs and fees do you charge?</Label>
          <Spacer marginTop="xxs" marginBottom="xs">
            <WizardBody>
              {isOptInTotalMonthlyPriceWithFeeEligible ? (
                <>
                  These details help renters know the real cost of renting your property, and helps you meet relevant
                  fee transparency laws.{' '}
                  <Anchor
                    target="_blank"
                    href="https://www.zillow.com/rentals-network/pricing-transparency/"
                    onClick={() => {
                      SingleFamilyCSEvents.LearnMore({
                        user,
                        currentProperty,
                        triggerObject: 'costs_and_fees',
                        triggerLocation: 'posting_path',
                      });
                    }}
                    data-eventid={SingleFamilyCSEvents.LearnMore.data_event_id}
                  >
                    Learn more
                  </Anchor>
                </>
              ) : (
                <>
                  We&apos;ve highlighted some of the most common fee{' '}
                  {canAccessTotalMonthlyPriceWithFee ? 'types' : 'categories'}. These details help renters know the real
                  cost of renting your property before they apply.
                </>
              )}
            </WizardBody>
          </Spacer>

          {canAccessTotalMonthlyPriceWithFee && <SFCostAndFeesTotalMonthlyPriceWithFeeAlert />}
          {isOptInTotalMonthlyPriceWithFeeEligible && <SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert />}

          <CostsAndFeesFormField displayedLocation="relisting" />
        </Spacer>
      )}
    </WizardFormSectionContainer>
  );
};

export default FormSectionContainer;
